import { useCallback, useState } from "react";
import { useNodeId } from "@xyflow/react";
import { useSetAtom } from "jotai";
import { Button } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { CREATE_NEW_AGENT_BUTTON_TEXT } from "@zapier/canvas-constants";
import {
  updateNodeSetter,
  saveCanvasSetter,
  closeSecondaryToolbarMenuSetter,
  createNewAgentSetter,
} from "@zapier/canvas-state";

import { useEmitResourceCreatedEvents } from "../../hooks/useEmitResourceCreatedEvents";
import { ErrorBoundary } from "../ErrorBoundary";

function CreateNewAgentButtonContent() {
  const nodeId = useNodeId();
  const [createNewAgentLoading, setCreateNewAgentLoading] = useState(false);
  const updateNode = useSetAtom(updateNodeSetter);
  const saveToBackend = useSetAtom(saveCanvasSetter);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  const createNewAgent = useSetAtom(createNewAgentSetter);
  const { emitEventOnAgentCreation } = useEmitResourceCreatedEvents();
  const [error, setError] = useState<Error | null>(null);

  const handleCreateNewAgent = useCallback(async () => {
    setCreateNewAgentLoading(true);
    try {
      const agentResponse = await createNewAgent();

      if (agentResponse && agentResponse.id) {
        const agentId = agentResponse.id;

        closeSecondaryMenu();

        const updatedNodes = updateNode({
          id: nodeId!,
          data: {
            agentId,
          },
        });

        saveToBackend({ nodes: updatedNodes });

        emitEventOnAgentCreation(agentId);
      }

      setError(null);
    } catch {
      setError(
        new Error(
          "We ran into an unexpected error trying to create a new agent. Try again.",
        ),
      );
    } finally {
      setCreateNewAgentLoading(false);
    }
  }, [
    createNewAgent,
    updateNode,
    nodeId,
    saveToBackend,
    emitEventOnAgentCreation,
    closeSecondaryMenu,
  ]);

  if (error) {
    throw error;
  }

  return (
    <Button
      variant="primary"
      size="compact"
      iconPosition="start"
      isPending={createNewAgentLoading}
      onPress={handleCreateNewAgent}
    >
      <Icon name="formAdd" />
      {CREATE_NEW_AGENT_BUTTON_TEXT}
    </Button>
  );
}

export function CreateNewAgentButton() {
  return (
    <ErrorBoundary>
      <CreateNewAgentButtonContent />
    </ErrorBoundary>
  );
}
